import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import FavoriteButton from "./FavoriteButton";
import { useLanguage } from "./LanguageContext";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import axios from "axios";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#ffffff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

/* ================= LAYOUT ================= */
const Section = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px 28px;
  margin-top: 20px;

  @media (max-width: 640px) {
    padding-left: 0;
    padding-right: 0;
  }

  h3 {
    margin: 8px 0 14px;
    font-size: 20px;
    font-weight: 900;
    color: ${COLORS.text};
    padding: 0 4px;
  }
`;

const CarouselWrap = styled.div`
  position: relative;
  width: 100%;
`;

const Track = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 8px 14px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 8px;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-thumb { background: #d9d9dc; border-radius: 8px; }

  @media (max-width: 640px) {
    gap: 12px;
    padding-left: 8px;
    padding-right: 8px;
  }
`;

const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 42px;
  width: 42px;
  border-radius: 50%;
  border: 1px solid ${COLORS.line};
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  box-shadow: ${COLORS.shadow};
  cursor: pointer;
  z-index: 2;
  transition: transform .15s ease, filter .15s ease, opacity .15s ease;

  &:hover { transform: translateY(-50%) scale(1.03); filter: brightness(.98); }
  &:disabled { opacity: .35; cursor: not-allowed; }

  @media (max-width: 640px) {
    height: 36px;
    width: 36px;
  }
`;
const ArrowLeft  = styled(ArrowBtn)` left: 6px; `;
const ArrowRight = styled(ArrowBtn)` right: 6px; `;

/* ================= CARD ================= */
const Card = styled.div`
  scroll-snap-align: start;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  min-width: clamp(200px, 24vw, 260px);
  max-width: 280px;
  padding: 12px;
  position: relative;
  transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    box-shadow: ${COLORS.shadow};
    transform: translateY(-2px);
    border-color: rgba(0,0,0,.12);
  }

  /* 👇 overlay მხოლოდ დაკლიკვაზე ჩანს ($active) */
  .hover-options {
    display: ${({ $active }) => ($active ? 'flex' : 'none')};
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6); backdrop-filter: blur(6px);
    align-items: center; justify-content: center; z-index: 40;
    border-radius: 16px;
    pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  }

  .view-btn{
    border: 1px solid ${COLORS.line};
    background:#fff;
    border-radius: 12px;
    height: 40px; padding: 0 14px;
    font-weight: 800; cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease;
    color: black;
  }
  .view-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }

  /* ❌ hover-ზე ავტომატურად აღარ ვაჩენთ overlay-ს — წავშალეთ rule */

  @media (max-width: 640px) {
    padding: 8px;
    border-radius: 12px;
    min-width: clamp(180px, 48vw, 220px);
  }
`;

const ImgWrap = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  background: ${COLORS.subtle};
  display: grid;
  place-items: center;
  overflow: hidden;
  margin-bottom: 10px;
  position: relative;

  @media (max-width: 640px) { margin-bottom: 6px; }
`;

const Image = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;     /* პროპორციულად ჩასვავს ჩარჩოში */
  object-position: center; /* სურვილისამებრ: ცენტრში გაასწორებს */
  display: block;          /* inline-gap აღარ იქნება */
  @media (max-width: 640px) { width: 80%; height: 80%; }
`;

const Title = styled.h2`
  font-size: 14px;
  margin: 4px 0 0;
  font-weight: 800;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) { font-size: 13px; }
`;

const Brand = styled.div`
  font-size: 12px;
  color: ${COLORS.dim};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;

  @media (max-width: 640px) { font-size: 11px; }
`;

const PriceRow = styled.div`
  margin-top: 8px;
  font-weight: 800;
  color: ${COLORS.text};
  font-size: 13px;

  .old {
    color: #9ca3af;
    text-decoration: line-through;
    margin-right: 6px;
  }

  @media (max-width: 640px) { font-size: 12px; }
`;

const IconBar = styled.div`
  position: absolute;
  left: 10px;
  top: 10px;
  display: flex;
  gap: 8px;
  z-index: 120;

  .icon-btn {
    width: 36px; height: 36px;
    border-radius: 10px;
    border: 1px solid ${COLORS.line};
    background: #fff;
    display: grid; place-items: center;
    cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover { transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }

  @media (max-width: 640px) {
    .icon-btn { width: 32px; height: 32px; }
  }
`;

const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px;
  background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite;
  z-index: 100;

  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
`;

// განსაზღვრე ML სტრატეგია თითო ვარიანტზე
function mlStrategy(ml, item){
  if (ml?.stock_strategy === "units") return "units";
  if (ml?.stock_strategy === "full")  return "full";
  return (item?.per_ml_stock && ml?.unit_stock != null) ? "units" : "full";
}

// ✅ FULL-FIRST picker: ჯერ full (პატარა ზომიდან ↑, მხოლოდ რაც full_stock-ს ჰყოფნის), ბოლოს units (unit_stock>0, იაფიდან ↑)
function pickAvailableMlCandidateFullFirst(p){
  const mls = Array.isArray(p.milliliters) ? p.milliliters : [];
  if (!mls.length) return null;

  const withStrat = mls
    .map(m => ({ ...m, _strategy: mlStrategy(m, p) }))
    .filter(m => Number(m.size) > 0);

  // 1) FULL — ყველაზე პატარა ზომა, რომელსაც pooled ჰყოფნის
  const fullCap = Number(p.full_stock ?? 0);
  const fulls = withStrat
    .filter(m => m._strategy === "full" && fullCap >= Number(m.size))
    .sort((a,b) => Number(a.size) - Number(b.size));
  if (fulls.length) return fulls[0];

  // 2) UNITS — გვაქვს მარაგი? ავიღოთ იაფი
  const units = withStrat
    .filter(m => m._strategy === "units" && Number(m.unit_stock ?? 0) > 0)
    .sort((a,b) => Number(a.sale_price ?? a.price) - Number(b.sale_price ?? b.price));
  return units[0] || null;
}

/* ================= COMPONENT ================= */
const FavoritePerfumes = ({ addToCart }) => {
      const { version } = useDataRefresh();

  const { t } = useLanguage();
  const [perfumes, setPerfumes] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const trackRef = useRef(null);
  const sectionRef = useRef(null);

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

// ✅ payload builder: იყენებს არჩეულ კანდიდატს (თუ არგუმენტად არ მიეცი, თვითონ მოძებნის FULL-FIRST)
function buildDefaultPerfumePayload(p, candidateArg){
  const m = candidateArg || pickAvailableMlCandidateFullFirst(p);
  if (!m) return null;

  const sizeNum  = Number(m.size);
  return {
    ...p,
    image: (Array.isArray(p.imageUrls) && p.imageUrls[0]) || p.image || "",
    quantities: { [String(sizeNum)]: 1 },                 // addToCart → extractPerfumeAdditions კითხულობს
    selected_ml: [{ size: sizeNum, quantity: 1 }],        // თავსებადობა mergePerfumeSelectedMl-თან
    selectedMilliliters: [{                               // თუ სადმე ამას კითხულობ
      size: sizeNum,
      price: Number(m.price),
      sale_price: (m.sale_price != null ? Number(m.sale_price) : null),
      images: Array.isArray(m.images) ? m.images : [],
      unit_stock: (m.unit_stock != null ? Number(m.unit_stock) : undefined),
      qty: 1,
    }],
    quantity: 1,
  };
}

 // 1) მონაცემების ჩატვირთვა mount-ზე და ყოველ DB ცვლილებაზე
 useEffect(() => {
   let cancelled = false;
   const fetchPerfumes = async () => {
     try {
       const res = await api.get(`${API_BASE_URL}/get_only_perfumes.php`, {
         // optional: cache-busting, audit, etc.
         params: { v: version },
       });
       const rows = Array.isArray(res.data) ? res.data : [];
       const sorted = rows
         .sort((a, b) => {
           // robust sort: created_at თუ არაა, fallback ენთა/ნუმერიკულ იდებზე
           const A = a.created_at ?? a.id ?? a.product_id ?? 0;
           const B = b.created_at ?? b.id ?? b.product_id ?? 0;
           // სცადე თარიღი → თუ არაა, სცადე რიცხვი
          const dt = new Date(B) - new Date(A);
           if (!Number.isNaN(dt) && dt !== 0) return dt;
           return Number(B) - Number(A);
         })
         .slice(0, 10);
       if (!cancelled) setPerfumes(sorted);
     } catch (e) {
       console.error("Error fetching perfumes:", e);
     }
   };
   fetchPerfumes();
   return () => { cancelled = true; };
 }, [version]);

 // 2) გარე-კლიკის listener — მხოლოდ ერთხელ
 useEffect(() => {
   const handleClickOutside = (e) => {
     if (sectionRef.current?.contains(e.target)) return;
     setActiveCard(null);
   };
   document.addEventListener("click", handleClickOutside, { passive: true });
   return () => document.removeEventListener("click", handleClickOutside);
 }, []);

  // ისრების აქტიობის სინქი
  const syncArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setAtStart(scrollLeft <= 2);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 2);
  };

  useEffect(() => {
    syncArrows();
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => syncArrows();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", syncArrows);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", syncArrows);
    };
  }, []);

  const getStep = () => {
    const el = trackRef.current;
    if (!el || !el.firstElementChild) return 276;
    const first = el.firstElementChild.getBoundingClientRect();
    const second = el.children?.[1]?.getBoundingClientRect();
    const gap = second ? Math.max(8, Math.round(second.left - first.right)) : 16;
    return Math.round(first.width + gap);
  };

  const scrollLeft = () => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: -getStep(), behavior: "smooth" });
  };

  const scrollRight = () => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: getStep(), behavior: "smooth" });
  };

  const handleCardClick = (id, e) => {
    e.stopPropagation();
    setActiveCard((prev) => (prev === id ? null : id));
  };

  return (
    <Section className="new-products" ref={sectionRef}>
      <h3>{t("newproducts.header")}</h3>

      <CarouselWrap>
        <ArrowLeft onClick={scrollLeft} disabled={atStart} aria-label="Scroll left">‹</ArrowLeft>

        <Track ref={trackRef}>
          {perfumes.map((perfume) => {
            const isMlBasedCard = ["perfume", "skin-care", "hair-and-body-care"].includes(perfume.category);
            const minMlSize =
              isMlBasedCard && Array.isArray(perfume.milliliters) && perfume.milliliters.length
                ? Math.min(
                    ...perfume.milliliters
                      .map((ml) => parseFloat(ml.size))
                      .filter((n) => !isNaN(n) && n > 0)
                  )
                : 0;

// უსაფრთხო მისაღები მნიშვნელობები
const fullStock = Number(perfume.full_stock ?? 0);
const mlList = Array.isArray(perfume.milliliters) ? perfume.milliliters : [];

// ყველაზე პატარა ჩამოსასხმელი მლ-ის ზომა უკვე გაქვს როგორც minMlSize
const canPourAnyMl = (minMlSize > 0) && (fullStock >= minMlSize);

// არის თუ არა хоть ერთი ერთეულად გაყიდვადი ვარიანტი მარაგში
const hasUnitVariantInStock = mlList.some(m => Number(m.unit_stock ?? 0) > 0);

// საბოლოო ლოგიკა
const showAddToCart =
  perfume.category === "accessories"
    ? fullStock >= 1
    : isMlBasedCard
      ? (canPourAnyMl || hasUnitVariantInStock)  // ← დამატებული პირობა
      : fullStock >= 1;

            const hasAnySale =
              (perfume.milliliters &&
                Array.isArray(perfume.milliliters) &&
                perfume.milliliters.some((ml) => Number(ml.sale_price) > 0 && Number(ml.sale_price) < Number(ml.price))) ||
              (perfume.sale_p && Number(perfume.sale_p) > 0 && Number(perfume.sale_p) < Number(perfume.price_p));

            const isActive = activeCard === perfume.product_id;

            return (
              <Card
                key={perfume.product_id}
                onClick={(e) => handleCardClick(perfume.product_id, e)}
                $active={isActive}
              >
                {/* Overlay — მხოლოდ დაკლიკვაზე ჩანს; overlay-ზე ცარიელ ადგილას დაკლიკებით დაიხუროს */}
                <div
                  className="hover-options"
                  onClick={(e) => { e.stopPropagation(); setActiveCard(null); }}
                >
                  <Link
                    to={`/product/${perfume.product_id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="view-btn">
                      {t("categorypage.viewmore") || "სრულად ნახვა"}
                    </button>
                  </Link>
                </div>

                {hasAnySale && <SaleBadge>{t("newproducts.sale")}</SaleBadge>}

                <CardLink to="#" onClick={(e) => e.preventDefault()}>
                  <ImgWrap>
                    <Image src={perfume.imageUrls?.[0]} alt={perfume.name} />
                  </ImgWrap>

                  <Title title={perfume.name}>{perfume.name}</Title>
                  <Brand title={perfume.brand}>{perfume.brand}</Brand>

                  <PriceRow>
                    {perfume.milliliters?.length > 0 ? (
                      (() => {
                        const original = perfume.milliliters.map((ml) => parseFloat(ml.price));
                        const sale = perfume.milliliters.map((ml) =>
                          parseFloat(ml.sale_price) || parseFloat(ml.price)
                        );
                        const minOriginal = Math.min(...original);
                        const maxOriginal = Math.max(...original);
                        const minSale = Math.min(...sale);
                        const maxSale = Math.max(...sale);
                        const changed = minSale < minOriginal || maxSale < maxOriginal;

                        return changed ? (
                          <>
                            <span className="old">
                              {minOriginal === maxOriginal
                                ? `${minOriginal} ${t("newproducts.currency")}`
                                : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
                            </span>
                            <span>
                              {minSale === maxSale
                                ? `${minSale} ${t("newproducts.currency")}`
                                : `${minSale} - ${maxSale} ${t("newproducts.currency")}`}
                            </span>
                          </>
                        ) : (
                          <span>
                            {minOriginal === maxOriginal
                              ? `${minOriginal} ${t("newproducts.currency")}`
                              : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
                          </span>
                        );
                      })()
                    ) : (
                      "N/A"
                    )}
                  </PriceRow>
                </CardLink>

                <IconBar>
                  {showAddToCart && (
 <button
   className="icon-btn"
   onClick={(e) => {
     e.stopPropagation();
     const payload = buildDefaultPerfumePayload(perfume);
     if (!payload) return;           // თუ არცერთი ზომა მიუწვდომელია
     addToCart(payload);
   }}
   aria-label={t("categorypage.addtocart") || "Add to cart"}
   title={t("categorypage.addtocart") || "Add to cart"}
 >
   <ShoppingCart size={18} />
 </button>                  )}
                  <div className="icon-btn" onClick={(e) => e.stopPropagation()}>
                    <FavoriteButton productId={perfume.product_id} initialIsFavorite={perfume.is_favorite} />
                  </div>
                </IconBar>
              </Card>
            );
          })}
        </Track>

        <ArrowRight onClick={scrollRight} disabled={atEnd} aria-label="Scroll right">›</ArrowRight>
      </CarouselWrap>

      <div className="all-perfumes-cont" style={{ textAlign: "center", marginTop: 12 }}>
        <Link
          to="/products?category=perfume"
          className="all-perfumes"
          style={{
            display: "inline-block",
            padding: "10px 16px",
            borderRadius: 12,
            background: COLORS.text,
            color: "#fff",
            textDecoration: "none",
            fontWeight: 800,
            border: `1px solid ${COLORS.line}`,
            boxShadow: COLORS.shadow,
          }}
        >
          {t("newproducts.viewall")}
        </Link>
      </div>
    </Section>
  );
};

export default FavoritePerfumes;
